// src/data/storage.ts
// -------------------
// Repository for the `zephyr-files` Supabase Storage bucket. Storage uses a
// different API surface (client.storage.from(bucket)) than the table repos
// (client.from(table)), so it lives in its own module — but it follows the same
// src/data/ convention: the bucket name + the default upload options live here
// once, not duplicated across the ~7 file-upload call sites. Uses
// getSupabaseClient() (now SupabaseClient<Database>); Storage isn't part of the
// Database generic, so there's no row typing involved here.

import { logger } from "../utils/logger";
import { getSupabaseClient } from "../utils/supabase";

// The zephyr-files bucket: uploaded docs / fee brochures / BYOD docs + the generated
// authority-doc PDFs. PRIVATE since db-168 (F3) — reads go via short-lived signed
// URLs (getFileSignedUrl), never a public CDN link; the owner-or-staff SELECT policy
// on storage.objects is what actually enforces access. (Invoices are a separate
// private bucket, zephyr-invoices / db-26.)
const BUCKET = "zephyr-files";

// The PRIVATE bucket for invoices (docs/db-26 + storage-access-restriction-plan).
// Invoices are stored by PATH here and opened via a short-lived signed URL; the
// public-CDN path does NOT work (the bucket is private + RLS-scoped).
const INVOICES_BUCKET = "zephyr-invoices";

type UploadOptions = { upsert?: boolean; contentType?: string };

// Upload a file into zephyr-files at `path` (overwriting by default). Returns
// { error } — callers branch on it; none read the upload's data payload. A null
// client yields an error result rather than throwing.
export async function uploadFile(
  path: string,
  file: File | Blob,
  options: UploadOptions = { upsert: true }
): Promise<{ error: { message: string } | null }> {
  const c = getSupabaseClient();
  if (!c) return { error: { message: "No Supabase client" } };
  const { error } = await c.storage.from(BUCKET).upload(path, file, options);
  if (error) logger.warn("storage upload failed:", error.message);
  return { error };
}

// The PUBLIC supplier-logo bucket (docs/db-38c). Logos are public branding, so
// they're served via public CDN URLs (used by the uniform authorisation PDF).
const SUPPLIER_LOGOS_BUCKET = "supplier-logos";

// Public URL for a supplier logo stored at `path` in the supplier-logos bucket.
export function getSupplierLogoUrl(path: string): string {
  const c = getSupabaseClient();
  if (!c || !path) return "";
  return c.storage.from(SUPPLIER_LOGOS_BUCKET).getPublicUrl(path).data.publicUrl;
}

// Upload/replace a supplier logo at `path` in the supplier-logos bucket (the
// SupplierAdmin screen; the staff/admin write policy is db-38c). Callers use a
// fresh timestamped path per upload so the public CDN never serves a stale
// cached logo, then persist the new path on suppliers.logo_path. Returns
// { error } like uploadFile.
export async function uploadSupplierLogo(
  path: string,
  file: File | Blob,
  options: UploadOptions = { upsert: true }
): Promise<{ error: { message: string } | null }> {
  const c = getSupabaseClient();
  if (!c) return { error: { message: "No Supabase client" } };
  const { error } = await c.storage.from(SUPPLIER_LOGOS_BUCKET).upload(path, file, options);
  if (error) logger.warn("storage supplier-logo upload failed:", error.message);
  return { error };
}

// ── Resources content images: the PRIVATE resource-images bucket (db-110 → db-185) ─
// The TipTap Resources editor uploads inline images here. db-185 flipped the bucket
// PRIVATE (5 MB + MIME whitelist), so images are read via a SIGNED url, not a public
// CDN link. Upload returns { error, path, url }: `path` is the stable object key to
// re-sign from; `url` is a freshly-signed url the editor shows immediately. The
// render-time signer (signResourceImagesInHtml) re-signs on every view, so a stored
// public OR expired-signed url still works — its object key is re-extracted and signed.
const RESOURCE_IMAGES_BUCKET = "resource-images";

// A short-lived signed URL for a resource image `path`. "" on no client / error.
export async function signResourceImage(path: string, expiresIn = 3600): Promise<string> {
  const c = getSupabaseClient();
  if (!c || !path) return "";
  const { data, error } = await c.storage
    .from(RESOURCE_IMAGES_BUCKET)
    .createSignedUrl(path, expiresIn);
  if (error) {
    logger.warn("storage resource-image sign failed:", error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}

export async function uploadResourceImage(
  path: string,
  file: File | Blob,
  options: UploadOptions = { upsert: true }
): Promise<{ error: { message: string } | null; path: string; url: string }> {
  const c = getSupabaseClient();
  if (!c) return { error: { message: "No Supabase client" }, path: "", url: "" };
  const { error } = await c.storage.from(RESOURCE_IMAGES_BUCKET).upload(path, file, options);
  if (error) {
    logger.warn("storage resource-image upload failed:", error.message);
    return { error, path: "", url: "" };
  }
  const url = await signResourceImage(path);
  return { error: null, path, url };
}

// A time-limited signed download URL for a stored object `path` (works regardless
// of bucket privacy / RLS, for the caller who can read the object). Returns "" on
// no client or error. Used by the CRM History view to offer the sent attachment.
export async function createSignedUrl(path: string, expiresIn = 3600): Promise<string> {
  const c = getSupabaseClient();
  if (!c) return "";
  const { data, error } = await c.storage.from(BUCKET).createSignedUrl(path, expiresIn);
  if (error) {
    logger.warn("storage createSignedUrl failed:", error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}

// zephyr-files is PRIVATE (db-168): documents / fee brochures / BYOD docs are read
// via a short-lived signed URL, NOT a public CDN link. `stored` may be either the
// new object PATH or a LEGACY full public URL (…/object/public/zephyr-files/<path>,
// persisted before db-168); this extracts the object key from a legacy URL, then
// signs it — so old requests keep working with no data migration. Returns "" on no
// client / error / RLS-deny (never throws). Default TTL 300s (5 min) — these are
// DFV-sensitive documents, so keep the window small (matches the invoice signer).
export async function getFileSignedUrl(stored: string, expiresIn = 300): Promise<string> {
  if (!stored) return "";
  let path = stored;
  const marker = "/zephyr-files/";
  const idx = stored.indexOf(marker);
  if (idx !== -1) {
    // Legacy full public URL → take the object key after the bucket, drop any query.
    path = stored.slice(idx + marker.length).split("?")[0];
    try {
      path = decodeURIComponent(path);
    } catch {
      /* leave the raw path if it isn't valid percent-encoding */
    }
  }
  return createSignedUrl(path, expiresIn);
}

// ── Invoices: the PRIVATE zephyr-invoices bucket (docs/db-26) ─────────────────
// Invoices are NOT public. Upload stores the object by `path`; the caller persists
// the PATH (not a URL), and rendering signs it on demand with getInvoiceSignedUrl.

// Upload an invoice file into the private bucket. Returns { error } like uploadFile.
export async function uploadInvoiceFile(
  path: string,
  file: File | Blob,
  options: UploadOptions = { upsert: true }
): Promise<{ error: { message: string } | null }> {
  const c = getSupabaseClient();
  if (!c) return { error: { message: "No Supabase client" } };
  const { error } = await c.storage.from(INVOICES_BUCKET).upload(path, file, options);
  if (error) logger.warn("storage invoice upload failed:", error.message);
  return { error };
}

// A short-lived signed URL to view/download a private invoice at `path` (default 5
// min — invoices are sensitive, so keep the window small). Returns "" on no client
// or error (e.g. RLS denies the caller).
export async function getInvoiceSignedUrl(path: string, expiresIn = 300): Promise<string> {
  const c = getSupabaseClient();
  if (!c) return "";
  const { data, error } = await c.storage.from(INVOICES_BUCKET).createSignedUrl(path, expiresIn);
  if (error) {
    logger.warn("storage invoice createSignedUrl failed:", error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}

// ── Upload timestamps (Files & Invoices panel) ───────────────────────────────
// Every stored object carries a created_at; we read it via storage.list() (the
// same authority the signers use) so the panel can show WHEN a file was uploaded.
// Works retroactively for existing files — nothing is persisted on the request.
// `stored` may be a bare object PATH or a LEGACY full public URL (same tolerance
// as the signers). Returns null on no client / error / not found (never throws),
// so the UI degrades to "no date" rather than breaking.
function storagePathFrom(stored: string, marker: string): string {
  const idx = stored.indexOf(marker);
  if (idx === -1) return stored;
  const raw = stored.slice(idx + marker.length).split("?")[0];
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

async function objectCreatedAt(
  bucket: string,
  stored: string,
  marker: string
): Promise<string | null> {
  if (!stored) return null;
  const c = getSupabaseClient();
  if (!c) return null;
  const path = storagePathFrom(stored, marker);
  const slash = path.lastIndexOf("/");
  const folder = slash >= 0 ? path.slice(0, slash) : "";
  const name = slash >= 0 ? path.slice(slash + 1) : path;
  const { data, error } = await c.storage.from(bucket).list(folder, { limit: 100, search: name });
  if (error) {
    logger.warn("objectCreatedAt list failed:", error.message);
    return null;
  }
  const rows = (data as Array<{ name: string; created_at?: string | null }> | null) ?? [];
  const hit = rows.find((o) => o.name === name);
  return hit?.created_at ?? null;
}

// created_at of a supporting document (zephyr-files) or an invoice (zephyr-invoices).
export async function getFileCreatedAt(stored: string): Promise<string | null> {
  return objectCreatedAt(BUCKET, stored, "/zephyr-files/");
}
export async function getInvoiceCreatedAt(stored: string): Promise<string | null> {
  return objectCreatedAt(INVOICES_BUCKET, stored, "/zephyr-invoices/");
}

// ── DFV Service PDF docs: the PRIVATE dfv-service-docs bucket (docs/db-128) ────
// Admin-only PDFs attached to a DFV Service, managed via a button → modal on the
// DFV Services detail screen. Stored by PATH under "<shelterId>/<file>"; listed by
// the shelter's prefix and opened via short-lived signed URLs (private bucket).
const DFV_SERVICE_DOCS_BUCKET = "dfv-service-docs";

export interface ServiceDoc {
  name: string; // the display file name (the object key within the shelter folder)
  path: string; // the full storage path ("<shelterId>/<name>")
}

// List a service's PDFs (newest first). Empty on no client / error.
export async function listServiceDocs(shelterId: string | undefined): Promise<ServiceDoc[]> {
  const c = getSupabaseClient();
  if (!c || !shelterId) return [];
  const { data, error } = await c.storage
    .from(DFV_SERVICE_DOCS_BUCKET)
    .list(shelterId, { sortBy: { column: "created_at", order: "desc" } });
  if (error) {
    logger.warn("listServiceDocs failed:", error.message);
    return [];
  }
  return (data ?? [])
    .filter((o) => o.name && o.name !== ".emptyFolderPlaceholder")
    .map((o) => ({ name: o.name, path: `${shelterId}/${o.name}` }));
}

// Upload a PDF for a service. A timestamp prefix keeps re-uploads of the same file
// name distinct (no silent overwrite). Returns { error, path }.
export async function uploadServiceDoc(
  shelterId: string,
  file: File
): Promise<{ error: { message: string } | null; path: string }> {
  const c = getSupabaseClient();
  if (!c) return { error: { message: "No Supabase client" }, path: "" };
  const safeName = file.name.replace(/[^\w.-]+/g, "_");
  const path = `${shelterId}/${Date.now()}-${safeName}`;
  const { error } = await c.storage
    .from(DFV_SERVICE_DOCS_BUCKET)
    .upload(path, file, { upsert: false, contentType: "application/pdf" });
  if (error) {
    logger.warn("uploadServiceDoc failed:", error.message);
    return { error, path: "" };
  }
  return { error: null, path };
}

// A short-lived signed URL (default 5 min) to open a service PDF. "" on error.
export async function getServiceDocSignedUrl(path: string, expiresIn = 300): Promise<string> {
  const c = getSupabaseClient();
  if (!c) return "";
  const { data, error } = await c.storage
    .from(DFV_SERVICE_DOCS_BUCKET)
    .createSignedUrl(path, expiresIn);
  if (error) {
    logger.warn("getServiceDocSignedUrl failed:", error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}

// ── Staff documents: the PRIVATE, SuperUser-only staff-docs bucket (db-208) ────
// Documents attached to a Zephyr Staff record (volunteer sign-on forms, director
// consents, ...). Stored by PATH under "<profileId>/<timestamp>-<file>"; listed by the
// profile's prefix and opened via short-lived signed URLs. No overwrite, no delete.
const STAFF_DOCS_BUCKET = "staff-docs";

export interface StaffDoc {
  name: string; // the object key within the profile folder ("<timestamp>-<file>")
  path: string; // the full storage path ("<profileId>/<name>")
}

// List a staff member's documents (newest first). Empty on no client / error.
export async function listStaffDocs(profileId: string | undefined): Promise<StaffDoc[]> {
  const c = getSupabaseClient();
  if (!c || !profileId) return [];
  const { data, error } = await c.storage
    .from(STAFF_DOCS_BUCKET)
    .list(profileId, { sortBy: { column: "created_at", order: "desc" } });
  if (error) {
    logger.warn("listStaffDocs failed:", error.message);
    return [];
  }
  return (data ?? [])
    .filter((o) => o.name && o.name !== ".emptyFolderPlaceholder")
    .map((o) => ({ name: o.name, path: `${profileId}/${o.name}` }));
}

// Upload a document for a staff member. `contentType` comes from staffDocContentType
// (worked out from the extension). A timestamp prefix keeps re-uploads of the same
// file name distinct; upsert is off (and the bucket has no UPDATE policy anyway).
export async function uploadStaffDoc(
  profileId: string,
  file: File,
  contentType: string
): Promise<{ error: { message: string } | null; path: string }> {
  const c = getSupabaseClient();
  if (!c) return { error: { message: "No Supabase client" }, path: "" };
  const safeName = file.name.replace(/[^\w.-]+/g, "_");
  const path = `${profileId}/${Date.now()}-${safeName}`;
  const { error } = await c.storage
    .from(STAFF_DOCS_BUCKET)
    .upload(path, file, { upsert: false, contentType });
  if (error) {
    logger.warn("uploadStaffDoc failed:", error.message);
    return { error, path: "" };
  }
  return { error: null, path };
}

// A short-lived signed URL (default 5 min) to open a staff document. "" on error.
export async function getStaffDocSignedUrl(path: string, expiresIn = 300): Promise<string> {
  const c = getSupabaseClient();
  if (!c) return "";
  const { data, error } = await c.storage.from(STAFF_DOCS_BUCKET).createSignedUrl(path, expiresIn);
  if (error) {
    logger.warn("getStaffDocSignedUrl failed:", error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}

// ── Resources documents: the PRIVATE resource-docs bucket (docs/db-138) ───────
// The Resources document store (forms/templates) linked from Resources pages via
// /resource-doc/<id>. PRIVATE, so downloads go through short-lived signed URLs and
// the internal-staff read policy is actually enforced (unlike resource-images,
// which is public because inline page images are low-sensitivity).
//
// The bucket itself carries a 5 MB file_size_limit + an allowed_mime_types
// whitelist (PDF + Office), so type/size rules hold server-side even if a client
// check is bypassed. (Was 25 MB in db-138; dropped to 5 MB by db-173 on John's
// call — finding B14. The matching client-side constant is
// RESOURCE_DOC_MAX_BYTES in services/resourceDocs.ts.)
const RESOURCE_DOCS_BUCKET = "resource-docs";

// Upload a document file for `docId`. The caller generates the id (crypto.randomUUID)
// BEFORE inserting the row, because the object is stored under that id — that's what
// keeps a Replace scoped to one document's folder. A timestamp prefix means a Replace
// never overwrites the previous file (upsert: false), so a mistaken replace is
// recoverable and no CDN staleness is possible. Returns { error, path }.
export async function uploadResourceDoc(
  docId: string,
  file: File
): Promise<{ error: { message: string } | null; path: string }> {
  const c = getSupabaseClient();
  if (!c) return { error: { message: "No Supabase client" }, path: "" };
  const safeName = file.name.replace(/[^\w.-]+/g, "_");
  const path = `${docId}/${Date.now()}-${safeName}`;
  const { error } = await c.storage
    .from(RESOURCE_DOCS_BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type || undefined });
  if (error) {
    logger.warn("uploadResourceDoc failed:", error.message);
    return { error, path: "" };
  }
  return { error: null, path };
}

// A short-lived signed URL (default 5 min, matching the invoice / service-doc
// convention) to download a stored document. "" on error — e.g. RLS denies a
// caseworker, or the object is gone.
//
// `downloadName` sets Content-Disposition: attachment on the signed URL, so the
// browser SAVES the file under that name instead of trying to render it. Without
// it, Chrome attempts an inline viewer for a .docx and fails with "Couldn't load
// plugin". Omit it for PDFs, which are worth viewing inline.
export async function getResourceDocSignedUrl(
  path: string,
  expiresIn = 300,
  downloadName?: string
): Promise<string> {
  const c = getSupabaseClient();
  if (!c) return "";
  const { data, error } = await c.storage
    .from(RESOURCE_DOCS_BUCKET)
    .createSignedUrl(path, expiresIn, downloadName ? { download: downloadName } : undefined);
  if (error) {
    logger.warn("getResourceDocSignedUrl failed:", error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}
